/**
 * PreviewBanner.jsx
 * ---------------------------------------------------------------------------
 * Slim "preview build" notice strip that renders at the top of every page
 * when the build was made with REACT_APP_PREVIEW_BANNER=true.
 *
 * Why this exists
 * ---------------
 * The deployed site is currently a WIP-preview shown to a small, operator-
 * curated audience (the public registration door is closed — see Phase C.5
 * of the registration kill-switch work). Every visitor who can actually log
 * in is, by construction, an internal/invited reviewer rather than a
 * customer. A site-wide banner sets correct expectations: "what you're
 * seeing is in flux; features and data may change without notice." It also
 * lets the same prod bundle be shown in screen-shares / demos without
 * anyone wondering whether they're looking at a finished product.
 *
 * Design choices
 * --------------
 * 1. ENV-GATED, NOT USER-GATED. Visibility comes from
 *    `process.env.REACT_APP_PREVIEW_BANNER`, baked into the bundle at
 *    build time. No Redux read, no JWT claim, no per-user flag. The cost
 *    on every page is therefore one boolean check at module-eval time and
 *    one `null` return if the flag is off — effectively free.
 *
 * 2. FAIL-CLOSED (opposite of the registration kill-switch). The banner
 *    only shows when the flag is the literal string "true". Any other
 *    value (undefined, "", "false") hides it. Reason: dev should never
 *    accidentally have this banner showing locally (it'd be noise during
 *    feature work), but prod also shouldn't have it accidentally if a
 *    repo-vars misconfiguration leaves the var empty — that would just
 *    revert to "no banner", which is the same user-visible behavior as
 *    dev. The active flip happens in .github/workflows/deploy-client.yml
 *    where prod builds set "true" deliberately.
 *
 * 3. Mounted OUTSIDE the RouterProvider in App.jsx (alongside BuildStamp)
 *    so it persists across every route — login, error, not-found — i.e.
 *    every visible surface, not just authenticated pages. Same rationale
 *    as BuildStamp.
 *
 * 4. NOT DISMISSABLE. A dismiss button would (a) need state storage to
 *    persist the dismissal, (b) immediately defeat the point ("operator
 *    is screen-sharing -> visitor dismissed banner -> demo looks polished
 *    in a misleading way"). The banner is 28px tall and uses muted styling
 *    so it doesn't fight for attention; that's the courtesy compromise
 *    rather than a dismiss UX.
 *
 * Flip procedure
 * --------------
 * See docs/preview-banner/README.md for the operator one-pager. tl;dr:
 *   - Dev: don't set the var. Banner stays hidden.
 *   - Prod: workflow file sets "true" explicitly. To turn it off at
 *           public-launch time, flip to "false" in the same PR that
 *           opens registration.
 * ---------------------------------------------------------------------------
 */

import React from 'react';
import '../styles/previewBanner.css';

/**
 * Read-once at module eval time. Treating this as a constant rather than
 * a per-render call keeps the component cheap and makes "is the banner
 * on?" answerable purely from the bundle (grep the minified output for
 * the banner string) — no need to inspect runtime state.
 *
 * Note the fail-CLOSED comparison (`=== 'true'`). Inverse of the
 * fail-OPEN convention used by isRegistrationEnabled() in
 * utils/featureFlags.js, which is intentional — see header.
 */
const PREVIEW_BANNER_ENABLED =
  process.env.REACT_APP_PREVIEW_BANNER === 'true';

function PreviewBanner() {
  if (!PREVIEW_BANNER_ENABLED) {
    return null;
  }

  return (
    <div
      className="preview-banner"
      role="status"
      aria-live="polite"
      data-testid="preview-banner"
    >
      <span className="preview-banner__label">Preview build</span>
      <span className="preview-banner__sep" aria-hidden="true">·</span>
      <span className="preview-banner__msg">
        Work in progress — features and data may change without notice.
      </span>
    </div>
  );
}

export default PreviewBanner;
